import { makeAutoObservable, runInAction, toJS } from "mobx";
import React, { useContext } from "react";
import { RootStore } from "../../shared/stores/RootStore";
import {
  CreateHlaForecastScenarioRequest,
  HlaForecastAction,
  HlaForecastScenario,
  UpdateHlaForecastScenarioRequest,
} from "../models/HlaForecastScenario";
import { HlaForecastApi } from "../services/HlaForecastApi";
import { ProjectApi } from "../services/ProjectApi";
import { HlaWay, HlaWayType } from "../../hla/models/HlaWay";
import { DistanceUnit, Score } from "@keita/lanepatrol-sdk";
import { buildHlaCoding, HlaCoding } from "../models/CalculateHlaScore";
import { HlaLane } from "../../hla/models/HlaLane";
import { toast } from "../../shared/views/CustomToaster";

export class HlaForecastStore {
  rootStore;
  actions: HlaForecastAction[] = [];
  scenarios: HlaForecastScenario[] = [];
  selectedScenario?: HlaForecastScenario;
  computingValues = false;
  computedValues?: {
    hlaWays: HlaWay[];
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    this.restoreOriginal();
    runInAction(() => {
      this.scenarios = [];
      this.computingValues = false;
    });
  }

  restoreOriginal() {
    runInAction(() => {
      this.actions = [];
      this.selectedScenario = undefined;
    });
    this.setComputedValues(undefined);
  }

  isForecastModeActivated() {
    return !!this.actions.length;
  }

  addAction(attributeChanges: Record<string, string | null>) {
    const { filterStore } = this.rootStore;
    runInAction(() => {
      this.actions = this.actions.concat([
        {
          attributeChanges,
          filters: {
            scoreFilters: filterStore.hlaScoreFilters,
            attributeFilters: filterStore.hlaAttributeFilters,
          },
        },
      ]);
    });
    this.computeValues();
  }

  removeActionByIndex(index: number) {
    runInAction(() => {
      this.actions = this.actions
        .filter((_, i) => i !== index)
        .map((change, i) =>
          i < index ? change : { ...change, results: undefined },
        );
    });
    this.computeValues();
  }

  setSelectedScenario(scenarioId: string | undefined) {
    const forecastScenario = this.scenarios.find(
      (scenario) => scenario.id === scenarioId,
    );

    if (forecastScenario) {
      runInAction(() => {
        this.selectedScenario = forecastScenario;
        this.actions = forecastScenario.actions;
      });
    } else {
      this.restoreOriginal();
    }

    this.computeValues();
  }

  async createScenario(request: CreateHlaForecastScenarioRequest) {
    const response = await HlaForecastApi.create(request);
    runInAction(() => {
      this.scenarios = [...this.scenarios, response.data];
      this.selectedScenario = response.data;
    });
    return response;
  }

  async updateScenario(id: string, request: UpdateHlaForecastScenarioRequest) {
    const response = await HlaForecastApi.update(id, request);
    runInAction(() => {
      this.scenarios = this.scenarios.map((item) =>
        item.id === id ? response.data : item,
      );
      this.selectedScenario = response.data;
    });
    return response;
  }

  async deleteScenario(id: string) {
    await HlaForecastApi.delete(id);
    runInAction(() => {
      this.scenarios = this.scenarios.filter((item) => item.id !== id);
      this.selectedScenario = undefined;
    });
  }

  async fetchScenarios(projectId: string, cancelIf?: () => boolean) {
    const response = await ProjectApi.listHlaForecastScenarios(projectId);
    if (cancelIf?.()) return;

    runInAction(() => {
      this.scenarios = response.data;
    });
  }

  private async computeValues() {
    const { hlaStore } = this.rootStore;

    if (!this.isForecastModeActivated()) {
      this.setComputedValues(undefined);
      return;
    }

    runInAction(() => {
      this.computingValues = true;
    });

    const forecastedWays = toJS(hlaStore.ways);

    try {
      for (const action of this.actions) {
        await this.computeAction(forecastedWays, action);
      }

      runInAction(() => {
        this.setComputedValues({
          hlaWays: forecastedWays,
        });
      });
    } catch (error) {
      toast("error", "An error occurred while simulating the scenario");
      console.error(error);
    } finally {
      runInAction(() => {
        this.computingValues = false;
      });
    }
  }

  private setComputedValues(values?: { hlaWays: HlaWay[] }) {
    runInAction(() => {
      this.computedValues = values;
    });

    const { mapStore, filterStore } = this.rootStore;
    filterStore.filterHlaWays();
    mapStore.hlaMapSource?.onStoreUpdate();
  }

  private applyComputedScoresToForecast(
    computedScores: Map<string, Score>,
    forecastedHlaWays: HlaWay[],
  ) {
    forecastedHlaWays.forEach((way) => {
      if (way.frontFacingLane) {
        way.frontFacingLane.score =
          computedScores?.get(way.frontFacingLane.id) ??
          way.frontFacingLane.score;
      }

      if (way.backFacingLane) {
        way.backFacingLane.score =
          computedScores?.get(way.backFacingLane.id) ??
          way.backFacingLane.score;
      }
    });
  }

  private async computeAction(
    ways: HlaWay[],
    action: HlaForecastAction,
  ): Promise<Map<string, Score>> {
    const { projectStore, filterStore } = this.rootStore;

    const affectedCodingsByLaneId: [string, HlaCoding][] = [];

    // use filters to check which lanes will be affected by this action
    ways.forEach((way) => {
      if (!filterStore.filterHlaWay(way)) return;

      const sortLane = (lane: HlaLane | null) => {
        if (lane && filterStore.filterHlaLane(lane)) {
          this.applyAction(way, lane, action);
          affectedCodingsByLaneId.push([lane.id, buildHlaCoding(way, lane)]);
        }
      };

      sortLane(way.frontFacingLane);
      sortLane(way.backFacingLane);
    });

    if (!action.computedScores) {
      // call API to get score for the affected lanes
      action.computedScores = await this.simulateChangeResults(
        affectedCodingsByLaneId,
        projectStore.distanceUnit,
      );
    }

    this.applyComputedScoresToForecast(action.computedScores!, ways);

    return action.computedScores;
  }

  private applyAction(way: HlaWay, lane: HlaLane, action: HlaForecastAction) {
    const attr = action.attributeChanges;

    if (attr.type) {
      way.type = attr.type as HlaWayType;
    }
    if (attr.averageDailyTraffic) {
      way.averageDailyTraffic = parseInt(attr.averageDailyTraffic);
    }
    if (attr.averageRoadSpeed) {
      way.averageRoadSpeed = parseInt(attr.averageRoadSpeed);
    }
    if (attr.heavyVehicleFlow) {
      way.heavyVehicleFlow = this.readNullableString(attr.heavyVehicleFlow);
    }
    if (attr.lit) {
      way.lit = attr.lit === "true";
    }
    if (attr.flowDirection) {
      lane.flowDirection = this.readNullableString(attr.flowDirection);
    }
    if (attr.facilityType) {
      lane.facilityType = this.readNullableString(attr.facilityType);
    }
    if (attr.facilityWidthPerDirection) {
      lane.facilityWidthPerDirection = this.readNullableString(
        attr.facilityWidthPerDirection,
      );
    }
    if (attr.lightSegregation) {
      lane.lightSegregation = attr.lightSegregation === "true";
    }
    if (attr.tramOrTrainRails) {
      lane.tramOrTrainRails = attr.tramOrTrainRails === "true";
    }

    if (attr["roadLane.left"]) {
      lane.adjacentFeaturesLeft.roadLane = this.readNullableString(
        attr["roadLane.left"],
      );
    }
    if (attr["roadLane.right"]) {
      lane.adjacentFeaturesRight.roadLane = this.readNullableString(
        attr["roadLane.right"],
      );
    }

    if (attr["vehicleParking.left"]) {
      lane.adjacentFeaturesLeft.vehicleParking = this.readNullableString(
        attr["vehicleParking.left"],
      );
    }
    if (attr["vehicleParking.right"]) {
      lane.adjacentFeaturesRight.vehicleParking = this.readNullableString(
        attr["vehicleParking.right"],
      );
    }

    if (attr["severeHazard.left"]) {
      lane.adjacentFeaturesLeft.severeHazard = this.readNullableString(
        attr["severeHazard.left"],
      );
    }
    if (attr["severeHazard.right"]) {
      lane.adjacentFeaturesRight.severeHazard = this.readNullableString(
        attr["severeHazard.right"],
      );
    }

    if (attr["objectOrLevelChange.left"]) {
      lane.adjacentFeaturesLeft.objectOrLevelChange = this.readNullableString(
        attr["objectOrLevelChange.left"],
      );
    }
    if (attr["objectOrLevelChange.right"]) {
      lane.adjacentFeaturesRight.objectOrLevelChange = this.readNullableString(
        attr["objectOrLevelChange.right"],
      );
    }

    if (attr["sidewalk.left"]) {
      lane.adjacentFeaturesLeft.sidewalk = this.readNullableString(
        attr["sidewalk.left"],
      );
    }
    if (attr["sidewalk.right"]) {
      lane.adjacentFeaturesRight.sidewalk = this.readNullableString(
        attr["sidewalk.right"],
      );
    }

    if (attr["numberOfLanes.left"]) {
      lane.adjacentFeaturesLeft.numberOfLanes = this.readNullableString(
        attr["numberOfLanes.left"],
      );
    }
    if (attr["numberOfLanes.right"]) {
      lane.adjacentFeaturesRight.numberOfLanes = this.readNullableString(
        attr["numberOfLanes.right"],
      );
    }

    if (attr["hardSegregation.left"]) {
      lane.adjacentFeaturesLeft.hardSegregation =
        attr["hardSegregation.left"] === "true";
    }
    if (attr["hardSegregation.right"]) {
      lane.adjacentFeaturesRight.hardSegregation =
        attr["hardSegregation.right"] === "true";
    }
  }

  private readNullableString<T>(strValue: string): T | null {
    if (strValue === "null") return null;
    return strValue as T;
  }

  private async simulateChangeResults(
    affectedCodingsByLaneId: [string, HlaCoding][],
    projectDistanceUnit: DistanceUnit,
  ) {
    const laneIds = affectedCodingsByLaneId.map((x) => x[0]);
    const codings = affectedCodingsByLaneId.map((x) => x[1]);

    const chunkSize = 500;
    var allResults: Score[] = [];

    for (let i = 0; i < codings.length; i += chunkSize) {
      const chunk = codings.slice(i, i + chunkSize);

      const response = await HlaForecastApi.simulateScores({
        distanceUnit: projectDistanceUnit,
        attributes: chunk,
      });

      allResults = allResults.concat(response.data);
    }

    return new Map(
      allResults.map((simulatedResult, i) => [laneIds[i], simulatedResult]),
    );
  }
}

interface ContextProps {
  hlaForecastStore: HlaForecastStore;
}
export const HlaForecastStoreContext = React.createContext({} as ContextProps);

export const useHlaForecastStoreContext = () =>
  useContext(HlaForecastStoreContext);
